import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Link,
  Step,
  StepLabel,
  Stepper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StepLayout from '../../components/StepLayout/StepLayout';
import {
  GUMS,
  MOCK_TRACKED_ORDERS,
  SERVICES,
  TICKET_PREFIX,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_ORDER,
} from '../../utils/mockData';

export default function OrderTracking() {
  const navigate = useNavigate();
  const [digits, setDigits] = useState('');
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  function handleChangeDigits(event) {
    setDigits(event.target.value.replace(/\D/g, '').slice(0, 3));
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (digits.length !== 3) {
      setOrder(null);
      setError('Merci de saisir les 3 derniers chiffres de votre ticket.');
      return;
    }
    const ticketNumber = `${TICKET_PREFIX}${digits}`;
    const found = MOCK_TRACKED_ORDERS[ticketNumber];
    if (!found) {
      setOrder(null);
      setError('Aucune commande ne correspond à ce numéro de ticket.');
      return;
    }
    setError('');
    setOrder({ ticketNumber, status: found.status, shoe: found.shoe });
  }

  function handleReset() {
    setDigits('');
    setOrder(null);
    setError('');
  }

  const statusIndex = order ? TICKET_STATUS_ORDER.indexOf(order.status) : -1;
  const activeStep =
    order?.status === 'DELIVERED' ? TICKET_STATUS_ORDER.length : statusIndex;

  return (
    <StepLayout title="Suivre ma commande" onBack={() => navigate('/')}>
      {!order && (
        <Stack
          component="form"
          onSubmit={handleSubmit}
          spacing={2}
          sx={{ maxWidth: 420, mb: 4 }}
        >
          <Typography color="text.secondary">
            Entrez les 3 derniers chiffres de votre numéro de ticket.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="N° de ticket"
              value={digits}
              onChange={handleChangeDigits}
              error={Boolean(error)}
              helperText={error}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">{TICKET_PREFIX}</InputAdornment>
                  ),
                },
                htmlInput: { inputMode: 'numeric', maxLength: 3 },
              }}
            />
            <Button type="submit" variant="contained">
              Valider
            </Button>
          </Stack>
        </Stack>
      )}

      {order && (
        <Stack spacing={3} sx={{ maxWidth: 480, mb: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">{order.ticketNumber}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {TICKET_STATUS_LABELS[order.status]}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2">
                {order.shoe.brand}
                {order.shoe.model ? ` — ${order.shoe.model}` : ''} — pointure{' '}
                {order.shoe.size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gomme : {GUMS.find((gum) => gum.id === order.shoe.gum)?.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Service :{' '}
                {SERVICES.find((service) => service.id === order.shoe.service)?.label} (
                {SERVICES.find((service) => service.id === order.shoe.service)?.price} €)
              </Typography>

              {order.status === 'QUOTE_SENT' && (
                <Button variant="contained" sx={{ mt: 2 }}>
                  Recevoir un lien de paiement par SMS
                </Button>
              )}
            </CardContent>
          </Card>

          <Stepper activeStep={activeStep} orientation="vertical">
            {TICKET_STATUS_ORDER.map((status) => (
              <Step key={status}>
                <StepLabel>{TICKET_STATUS_LABELS[status]}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Link component="button" type="button" underline="hover">
            Je souhaite être contacté par l'atelier
          </Link>

          <Button variant="outlined" onClick={handleReset}>
            Suivre une autre commande
          </Button>
        </Stack>
      )}

      <Button variant="text" onClick={() => navigate('/')}>
        Retour à l'accueil
      </Button>
    </StepLayout>
  );
}
